/**
 * @fileoverview React context for interactive chat functionality.
 * Provides sendMessage callback and enabled state to tool renderers.
 * Supports a registration pattern: the parent renders the provider,
 * and Chat registers its submitChat via registerSendMessage after mounting.
 */

'use client';

import { createContext, useCallback, useContext, useRef, type ReactNode } from 'react';

interface InteractiveChatContextValue {
  /** Whether interactive chat mode is enabled */
  enabled: boolean;
  /** Send a message as the user (maps to useChat().append) */
  sendMessage: (text: string) => void;
  /** Register the actual send function from Chat (called once on mount) */
  registerSendMessage: (fn: (text: string) => void) => void;
}

const InteractiveChatContext = createContext<InteractiveChatContextValue>({
  enabled: false,
  sendMessage: () => {},
  registerSendMessage: () => {},
});

interface InteractiveChatProviderProps {
  enabled: boolean;
  children: ReactNode;
}

/**
 * Provides interactive chat capabilities to descendant tool renderers.
 * Rendered by the parent (InteractiveChat) around the Chat component.
 * Chat registers its submitChat via registerSendMessage on mount.
 */
export function InteractiveChatProvider({
  enabled,
  children,
}: InteractiveChatProviderProps) {
  const sendMessageRef = useRef<(text: string) => void>(() => {});

  const registerSendMessage = useCallback((fn: (text: string) => void) => {
    sendMessageRef.current = fn;
  }, []);

  const sendMessage = useCallback((text: string) => {
    sendMessageRef.current(text);
  }, []);

  return (
    <InteractiveChatContext.Provider value={{ enabled, sendMessage, registerSendMessage }}>
      {children}
    </InteractiveChatContext.Provider>
  );
}

/**
 * Hook to access interactive chat capabilities from tool renderers.
 */
export function useInteractiveChat() {
  return useContext(InteractiveChatContext);
}
